/**
 * Utility functions for working with YouTube videos
 */

/**
 * Extracts the YouTube video ID from a URL
 * @param {string} url - YouTube URL
 * @returns {string|null} YouTube video ID or null if not found
 */
export const extractVideoId = (url) => {
  if (!url) return null;
  
  // First check for YouTube Shorts format
  const shortsRegex = /(?:youtube\.com\/shorts\/|youtu\.be\/shorts\/)([a-zA-Z0-9_-]{11})/i;
  const shortsMatch = url.match(shortsRegex);
  if (shortsMatch && shortsMatch[1]) {
    return shortsMatch[1];
  }
  
  // Handle different YouTube URL formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  
  return (match && match[2].length === 11) ? match[2] : null;
};

/**
 * Checks if a YouTube URL is a Shorts video
 * @param {string} url - YouTube URL
 * @returns {boolean} True if the URL is a YouTube Short
 */
export const isYouTubeShort = (url) => {
  if (!url) return false;
  
  // Check if the URL contains '/shorts/' which indicates a YouTube Short
  // Or if there are other indicators in the URL
  return url.toLowerCase().includes('/shorts/') || 
         url.toLowerCase().includes('vertical') || 
         url.toLowerCase().includes('portrait');
};

/**
 * Determines the format of a YouTube video based on URL and metadata
 * @param {string} url - YouTube URL
 * @param {Object} metadata - Optional metadata that might contain format information
 * @returns {string} Video format: 'landscape', 'portrait', or 'square'
 */
export const determineVideoFormat = (url, metadata = {}) => {
  // Check explicit format in metadata
  if (metadata.videoOrientation) {
    return metadata.videoOrientation;
  }
  
  // Check for YouTube Shorts
  if (isYouTubeShort(url)) {
    return 'portrait';
  }
  
  // Check for square format hints
  if (url.toLowerCase().includes('square') || 
      metadata.aspectRatio === '1:1') {
    return 'square';
  }
  
  // Default to landscape format (most common)
  return 'landscape';
};

/**
 * Creates YouTube embed options with appropriate parameters
 * @param {Object} options - Custom options to override defaults
 * @returns {Object} YouTube player options
 */
export const createYouTubeOptions = (options = {}) => {
  // Default options for YouTube embeds
  const defaultOptions = {
    height: '100%',
    width: '100%',
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: 0,
      modestbranding: 1,
      rel: 0,
      origin: window.location.origin,
    },
  };
  
  return {
    ...defaultOptions,
    ...options,
    playerVars: {
      ...defaultOptions.playerVars,
      ...(options.playerVars || {}),
    },
  };
};

/**
 * Formats a YouTube URL for embedding
 * @param {string} videoId - YouTube video ID
 * @param {Object} options - Embed options
 * @returns {string} Formatted embed URL
 */
export const getEmbedUrl = (videoId, options = {}) => {
  if (!videoId) return '';
  
  const baseUrl = `https://www.youtube.com/embed/${videoId}`;
  const params = [];
  
  // Add optional parameters
  if (options.autoplay) params.push('autoplay=1');
  if (options.mute) params.push('mute=1');
  if (options.modestBranding !== false) params.push('modestbranding=1');
  if (options.showInfo === false) params.push('showinfo=0');
  if (options.rel === false) params.push('rel=0');
  if (options.startTime) params.push(`start=${options.startTime}`);
  
  return params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;
};

/**
 * Gets a thumbnail URL for a YouTube video
 * @param {string} videoId - YouTube video ID
 * @param {string} quality - Thumbnail quality ('default', 'hqdefault', 'mqdefault', 'sddefault', 'maxresdefault')
 * @returns {string} Thumbnail URL
 */
export const getVideoThumbnail = (videoId, quality = 'hqdefault') => {
  if (!videoId) return '';
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};
